import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/api/content.api';
import { LANGUAGES, type GameMode, type Language, type UiTranslationEntry } from '@business-game/shared';

export const AdminPlayerTextsPage: React.FC = () => {
  const [language, setLanguage] = useState<Language>('fr');
  const [namespace, setNamespace] = useState<string>('');
  const [gameMode, setGameMode] = useState<GameMode | ''>('');
  const [entries, setEntries] = useState<UiTranslationEntry[]>([]);
  const [search, setSearch] = useState('');

  const loadData = async () => {
    const data = await contentApi.listUiTranslations(language, namespace || undefined, gameMode || undefined);
    setEntries(data);
  };

  useEffect(() => {
    void loadData();
  }, [language, namespace, gameMode]);

  const filteredEntries = useMemo(
    () => entries.filter((entry) => `${entry.namespace}.${entry.key}`.toLowerCase().includes(search.toLowerCase())),
    [entries, search]
  );

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Textes joueur</div>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Textes d'interface</h1>
            <p className="mt-2 text-sm text-slate-500">
              Modifiez les textes d'interface utilises par le parcours joueur, les modales et les ecrans finaux.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-4">
            <FilterCard label="Langue">
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value as Language)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                {LANGUAGES.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </FilterCard>

            <FilterCard label="Namespace">
              <input
                value={namespace}
                onChange={(event) => setNamespace(event.target.value)}
                placeholder="ex: game"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
            </FilterCard>

            <FilterCard label="Mode">
              <select
                value={gameMode}
                onChange={(event) => setGameMode((event.target.value || '') as GameMode | '')}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Global</option>
                <option value="teamlab">TeamLab</option>
                <option value="leadlab">LeadLab</option>
              </select>
            </FilterCard>

            <FilterCard label="Recherche">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher une cle"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
            </FilterCard>
          </div>

          <div className="space-y-4">
            {filteredEntries.map((entry) => (
              <UiTranslationCard key={entry.id} entry={entry} onSaved={loadData} />
            ))}
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const UiTranslationCard: React.FC<{
  entry: UiTranslationEntry;
  onSaved: () => Promise<void>;
}> = ({ entry, onSaved }) => {
  const [value, setValue] = useState(entry.value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(entry.value);
  }, [entry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentApi.upsertUiTranslation({
        namespace: entry.namespace,
        key: entry.key,
        language: entry.language,
        value,
        gameMode: entry.gameMode,
      });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <SurfaceCard className="p-5 hover:-translate-y-0.5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-indigo-700">
          {entry.namespace}.{entry.key}
        </span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-600">
          {entry.language}
        </span>
        {entry.gameMode && (
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-indigo-700">
            {entry.gameMode}
          </span>
        )}
      </div>

      <textarea
        value={value}
        onChange={(event) => setValue(event.target.value)}
        className="mt-4 min-h-24 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
      />

      <button
        type="button"
        onClick={() => void handleSave()}
        className="mt-4 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95 disabled:opacity-50"
        disabled={saving}
      >
        Sauver texte
      </button>
    </SurfaceCard>
  );
};

const FilterCard: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <SurfaceCard className="p-4">
    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </SurfaceCard>
);

const SurfaceCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-indigo-200 hover:shadow-lg ${className}`.trim()}
  >
    {children}
  </div>
);
